import type { GitpodAPI } from "@/api";
import { useEnvironmentClassMap, type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { useListGroups } from "@/queries/group-queries";
import { useOrganization } from "@/queries/organization-queries";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { toPlainMessage, type PartialMessage, type PlainMessage } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    keepPreviousData,
    queryOptions,
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    type QueryClient,
} from "@tanstack/react-query";
import {
    EnvironmentInitializer_Spec,
    GitInitializer_CloneTargetMode,
    type EnvironmentInitializer,
    type GitInitializer,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { ResourceOperation, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { PaginationRequest } from "gitpod-next-api/gitpod/v1/pagination_pb";
import {
    ListProjectsRequest_Filter,
    type ListProjectEnvironmentClassesRequest,
    type Project,
    type ProjectEnvironmentClass,
    type ProjectPolicy,
    type ProjectPrebuildConfiguration,
    type ProjectRole,
} from "gitpod-next-api/gitpod/v1/project_pb";
import { useMemo } from "react";

export const projectsQueryKey = {
    list: (filter?: PartialMessage<ListProjectsRequest_Filter>) =>
        keyWithPrincipal(["projects", "list", "all", { filter }]),
    listPaginated: (filter?: PartialMessage<ListProjectsRequest_Filter>) =>
        keyWithPrincipal(["projects", "list", "paginated", { filter }]),
    get: (projectId?: string) => keyWithPrincipal(["projects", { projectId }]),
    listPolicies: (projectId?: string) => keyWithPrincipal(["projectsPolicies", "list", { projectId }]),
    listEnvironmentClasses: (projectId?: string) =>
        keyWithPrincipal(["projectEnvironmentClasses", "list", { projectId }]),
    listEnvironmentClassEntries: (projectId?: string) =>
        keyWithPrincipal(["projectEnvironmentClassEntries", "list", { projectId }]),
};

export type PlainProject = PlainMessage<Project>;

export function toPlainProject(project: Project): PlainProject {
    return toPlainMessage(project);
}

export type PlainProjectPolicy = PlainMessage<ProjectPolicy>;

export function toPlainProjectPolicy(policy: ProjectPolicy): PlainProjectPolicy {
    return toPlainMessage(policy);
}

export type PlainProjectEnvironmentClass = PlainMessage<ProjectEnvironmentClass>;

export function toPlainProjectEnvironmentClass(envClass: ProjectEnvironmentClass): PlainProjectEnvironmentClass {
    return toPlainMessage(envClass);
}

export const handleProjectEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    console.debug(`[EventHandler] Project event:`, {
        operation: evt.operation,
        resourceId: evt.resourceId,
    });

    if (evt.operation === ResourceOperation.UPDATE) {
        const project = await refetchProject(api, evt.resourceId);
        setProjectInCache(client, evt.resourceId, project ? toPlainProject(project) : undefined);
        console.debug(`[EventHandler] Project ${evt.resourceId} updated in cache`);
    }
    if (evt.operation === ResourceOperation.CREATE) {
        // Invalidate all list() caches (with different filter parameters)
        // Use prefix-based matching by removing the dynamic filter parameter
        await client.invalidateQueries({ queryKey: projectsQueryKey.list().slice(0, -1) });
        // Invalidate all listPaginated() caches (with different search filters)
        await client.invalidateQueries({ queryKey: projectsQueryKey.listPaginated().slice(0, -1) });
        console.debug(`[EventHandler] Project ${evt.resourceId} created, lists invalidated`);
    }
    if (evt.operation === ResourceOperation.DELETE) {
        setProjectInCache(client, evt.resourceId, undefined);
        console.debug(`[EventHandler] Project ${evt.resourceId} deleted from cache`);
    }
};

const refetchProject = async (api: GitpodAPI, projectId: string) => {
    try {
        const response = await api.projectService.getProject({ projectId });
        return response.project;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        // this might happen if the project was deleted in the meantime
        if (!isNotFound) {
            console.error("Failed to refetch project", projectId);
        }
    }
};

export function setProjectInCache(client: QueryClient, projectId: string, project?: PlainProject) {
    // Setting the query data to undefined means we skip the update, so we're explicitly setting it to null
    // So we can clear the cache on deletion.
    client.setQueryData(projectsQueryKey.get(projectId), project ? project : null);

    // Update infinite query data structure
    type InfiniteQueryData = {
        pages: { projects: PlainProject[]; nextToken?: string | null }[];
        pageParams: unknown[];
    };

    const updateInfiniteQueryData = (currentData: unknown) => {
        const infiniteData = currentData as InfiniteQueryData | undefined;

        const isNewProject =
            !!project && !infiniteData?.pages?.some((page) => page.projects.some((p) => p.id === projectId));

        // Insert the new project at the beginning of the first page
        if (isNewProject) {
            const newFirstPage = {
                nextToken: null,
                ...infiniteData?.pages[0],
                projects: [project, ...(infiniteData?.pages[0]?.projects ?? [])],
            };
            const pages = [newFirstPage, ...(infiniteData?.pages?.slice(1) ?? [])];
            return {
                pageParams: [""],
                ...infiniteData,
                pages,
            };
        }

        // If no current data, return empty structure
        // Hint: this is only the case when an update event is received while the list isn't loaded yet
        if (!infiniteData) {
            return {
                pages: [{ projects: [], nextToken: null }],
                pageParams: [""],
            };
        }

        // Update the existing project
        const updatedPages = infiniteData.pages.map((page) => {
            if (!page?.projects) return page;

            // updates or deletes the existing project
            const updatedProjects = page.projects
                .map((p) => (p.id === projectId ? project : p))
                .filter((w): w is PlainProject => !!w);

            return {
                ...page,
                projects: updatedProjects,
            };
        });

        return {
            ...infiniteData,
            pages: updatedPages,
        };
    };

    // Update the default list() cache (creates if doesn't exist)
    client.setQueryData(projectsQueryKey.list({ projectIds: [], runnerIds: [] }), updateInfiniteQueryData);

    // Update all other list() caches with different filter parameters
    // Use prefix-based matching by removing the dynamic filter parameter
    client.setQueriesData({ queryKey: projectsQueryKey.list().slice(0, -1) }, updateInfiniteQueryData);

    // Update all listPaginated() caches (with different search filters)
    // Use prefix-based matching by removing the dynamic filter parameter
    client.setQueriesData({ queryKey: projectsQueryKey.listPaginated().slice(0, -1) }, updateInfiniteQueryData);
}

function setProjectPolicyInCache(client: QueryClient, projectId: string, groupId: string, policy?: ProjectPolicy) {
    client.setQueryData(
        projectsQueryKey.listPolicies(projectId),
        (currentData?: { policies: PlainProjectPolicy[] }) => {
            if (!currentData) {
                return currentData;
            }
            return {
                ...currentData,
                projectId,
                policies: currentData.policies
                    .map((p) => {
                        if (p.groupId !== groupId) {
                            return p;
                        }
                        return policy;
                    })
                    .filter((w) => !!w),
            };
        },
    );
}

export const useCreateProject = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({
            name,
            contextURL,
            devcontainerFilePath,
            automationsFilePath,
            gitConfig,
        }: {
            name: string;
            contextURL: string;
            devcontainerFilePath?: string;
            automationsFilePath?: string;
            gitConfig?: {
                remoteUrl: string;
                ref: string;
                upstreamRemoteUrl?: string;
                checkoutLocation?: string;
            };
        }) => {
            // Create initializer specs based on whether we have Git config
            let initializers: EnvironmentInitializer_Spec[] = [];
            if (gitConfig) {
                initializers = [
                    new EnvironmentInitializer_Spec({
                        spec: {
                            case: "git",
                            value: {
                                remoteUri: gitConfig.remoteUrl,
                                targetMode: GitInitializer_CloneTargetMode.REMOTE_BRANCH,
                                cloneTarget: gitConfig.ref,
                                upstreamRemoteUri: gitConfig.upstreamRemoteUrl || "",
                                checkoutLocation: gitConfig.checkoutLocation || "",
                            } as GitInitializer,
                        },
                    }),
                ];
            } else {
                initializers = [
                    new EnvironmentInitializer_Spec({
                        spec: { case: "contextUrl", value: { url: contextURL } },
                    }),
                ];
            }

            const { project } = await api.projectService.createProject({
                name,
                initializer: {
                    specs: initializers,
                },
                devcontainerFilePath,
                automationsFilePath,
            });

            if (!project) {
                throw new Error("Failed to create project");
            }

            return toPlainProject(project);
        },
        onSuccess: (plainProject) => {
            // We're invalidating list queries based on events, so no need to invalidate here.
            setProjectInCache(client, plainProject.id, plainProject);
        },
    });
};

export const useCreateProjectFromEnvironment = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ projectName, environmentId }: { projectName: string; environmentId: string }) => {
            const { project } = await api.projectService.createProjectFromEnvironment({
                name: projectName,
                environmentId,
            });

            if (!project) {
                throw new Error("Failed to create project");
            }

            return toPlainProject(project);
        },
        onSuccess: (plainProject) => {
            // We're invalidating list queries based on events, so no need to invalidate here.
            setProjectInCache(client, plainProject.id, plainProject);
        },
    });
};

export const useUpdateProject = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            projectId,
            name,
            initializer,
            devcontainerFilePath,
            automationsFilePath,
            prebuildConfiguration,
        }: {
            projectId: string;
            name?: string;
            initializer?: PartialMessage<EnvironmentInitializer>;
            devcontainerFilePath?: string;
            automationsFilePath?: string;
            prebuildConfiguration?: PartialMessage<ProjectPrebuildConfiguration>;
        }) => {
            const { project } = await api.projectService.updateProject({
                projectId,
                name,
                initializer,
                devcontainerFilePath,
                automationsFilePath,
                prebuildConfiguration,
            });

            return toPlainProject(project!);
        },
        onSuccess: (plainProject) => {
            // We're invalidating queries based on events, so no need to invalidate here.
            setProjectInCache(queryClient, plainProject.id, plainProject);
        },
    });
};

export const useListProjects = (
    { enabled, projectIds, runnerIds }: { enabled: boolean; projectIds?: string[]; runnerIds?: string[] } = {
        enabled: true,
        projectIds: [],
        runnerIds: [],
    },
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const infiniteQuery = useInfiniteQuery({
        queryKey: projectsQueryKey.list({ projectIds, runnerIds }),
        queryFn: async ({ pageParam = "" }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const response = await api.projectService.listProjects({
                pagination: new PaginationRequest({ token: pageParam }),
                filter: new ListProjectsRequest_Filter({
                    projectIds,
                    runnerIds,
                }),
            });
            if (response.pagination?.nextToken) {
                // THIS triggers auto-pagination
                requestAnimationFrame(() => {
                    infiniteQuery.fetchNextPage().catch(console.error);
                });
            }
            return {
                projects: response.projects.map(toPlainProject),
                nextToken: response.pagination?.nextToken || null,
            };
        },
        getNextPageParam: (lastPage) => lastPage.nextToken,
        initialPageParam: "",
        enabled: enabled && Boolean(user),
        staleTime: 5 * 60 * 1000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        refetchOnMount: false,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // This is important for deduplication
        notifyOnChangeProps: "all",
        // Use select to transform data efficiently
        select: (data) => {
            const allProjects = data.pages.flatMap((page) => page.projects);

            return {
                projects: allProjects,
                isComplete: !data.pages[data.pages.length - 1]?.nextToken,
            };
        },
    });

    // Memoize the entire return object
    return useMemo(
        () => ({
            data: infiniteQuery.data,
            isLoading: infiniteQuery.isLoading,
            isPending: infiniteQuery.isPending,
            isError: infiniteQuery.isError,
            error: infiniteQuery.error,
            isSuccess: infiniteQuery.isSuccess,
            refetch: infiniteQuery.refetch,
            // Pagination specific
            fetchNextPage: infiniteQuery.fetchNextPage,
            hasNextPage: infiniteQuery.hasNextPage,
            isFetchingNextPage: infiniteQuery.isFetchingNextPage,
            isComplete: infiniteQuery.data?.isComplete,
            // Extended metadata
            _paginationMeta: {
                isLoading: infiniteQuery.isLoading,
                isFetchingNextPage: infiniteQuery.isFetchingNextPage,
                hasNextPage: infiniteQuery.hasNextPage,
            },
        }),
        [
            infiniteQuery.data,
            infiniteQuery.isLoading,
            infiniteQuery.isPending,
            infiniteQuery.isError,
            infiniteQuery.error,
            infiniteQuery.isSuccess,
            infiniteQuery.refetch,
            infiniteQuery.fetchNextPage,
            infiniteQuery.hasNextPage,
            infiniteQuery.isFetchingNextPage,
        ],
    );
};

export type UseListProjectsPaginatedParams = {
    page?: string;
    search?: string;
    runnerIds?: string[];
};

export const useListProjectsPaginated = ({ page, search, runnerIds }: UseListProjectsPaginatedParams = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useInfiniteQuery({
        queryKey: projectsQueryKey.listPaginated({
            search,
            runnerIds,
        }),
        queryFn: async ({ pageParam }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const response = await api.projectService.listProjects({
                pagination: new PaginationRequest({ token: pageParam }),
                filter: new ListProjectsRequest_Filter({
                    search,
                    runnerIds,
                }),
            });

            return {
                projects: response.projects.map(toPlainProject),
                pagination: response.pagination,
            };
        },
        initialPageParam: page,
        getNextPageParam: (lastPage) => {
            if (!lastPage.pagination?.nextToken) {
                return undefined;
            }
            return lastPage.pagination.nextToken || undefined;
        },
        enabled: !!user,
        staleTime: 5 * 60 * 1000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        placeholderData: keepPreviousData,
    });
};

/**
 * The query for getting a single project by ID.
 * It's split from useProject to allow for re-use (like we do in the archived sidebar)
 */
export const getProjectQuery = (api: GitpodAPI, projectId?: string) => {
    return queryOptions({
        queryKey: projectsQueryKey.get(projectId),
        queryFn: async (): Promise<PlainProject> => {
            if (!projectId) {
                throw new Error("No project ID provided");
            }

            const { project } = await api.projectService.getProject({ projectId });
            if (!project) {
                throw new Error("Project not found");
            }

            return toPlainProject(project);
        },
        enabled: !!projectId,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useProject = (projectId?: string) => {
    const api = useGitpodAPI();

    return useQuery<PlainProject, Error>(getProjectQuery(api, projectId));
};

export const useDeleteProject = () => {
    const queryClient = useQueryClient();
    const api = useGitpodAPI();

    return useMutation({
        mutationFn: async ({ projectId }: { projectId: string }) => {
            await api.projectService.deleteProject({
                projectId,
            });
        },
        onSuccess: (_, { projectId }) => {
            // We're invalidating queries based on events, so no need to invalidate here.
            setProjectInCache(queryClient, projectId, undefined);
        },
    });
};

export const useCreateProjectPolicy = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ projectId, groupId, role }: { projectId: string; groupId: string; role: ProjectRole }) => {
            const { policy } = await api.projectService.createProjectPolicy({
                projectId,
                groupId,
                role,
            });

            return toPlainProjectPolicy(policy!);
        },
        onSettled: async (_, __, { projectId }) => {
            await client.invalidateQueries({ queryKey: projectsQueryKey.listPolicies(projectId) });
        },
    });
};

export const useListProjectPolicies = (projectId?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: projectsQueryKey.listPolicies(projectId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const policies = await autoPaginate(
                (params) => api.projectService.listProjectPolicies(params),
                { projectId },
                (response) => response.policies,
            );

            return {
                policies: policies.map(toPlainProjectPolicy),
                projectId,
            };
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user && !!projectId,
    });
};

export const useDeleteProjectPolicy = () => {
    const queryClient = useQueryClient();
    const api = useGitpodAPI();

    return useMutation({
        mutationFn: async ({ projectId, groupId }: { projectId: string; groupId: string }) => {
            await api.projectService.deleteProjectPolicy({
                projectId,
                groupId,
            });

            setProjectPolicyInCache(queryClient, projectId, groupId, undefined);
        },
        onSettled: async (_, __, { projectId }) => {
            await queryClient.invalidateQueries({ queryKey: projectsQueryKey.listPolicies(projectId) });
        },
    });
};

export const useListProjectsPolicies = (projectIds?: string[]) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQueries({
        queries: projectIds
            ? projectIds.map((projectId) => {
                  return {
                      queryKey: projectsQueryKey.listPolicies(projectId),
                      queryFn: async () => {
                          if (!user) {
                              throw new Error("User not authenticated");
                          }

                          const policies = await autoPaginate(
                              (params) => api.projectService.listProjectPolicies(params),
                              { projectId },
                              (response) => response.policies,
                          );

                          return {
                              policies: policies.map(toPlainProjectPolicy),
                              projectId,
                          };
                      },
                      staleTime: 60_000,
                      throwOnError: defaultThrowOnError,
                      retry: defaultRetry,
                  };
              })
            : [],
    });
};

export type ProjectState = {
    projectId: string;
    orgId: string;
    orgMembersGroupId: string;
    shared: boolean | undefined;
    environmentClasses: PlainProjectEnvironmentClass[] | undefined;
};

export const useProjectState = (
    projectId?: string,
): { isLoading: boolean; error: Error | undefined; data: ProjectState | undefined } => {
    const { data: organization, isPending: isLoadingOrganization, error: organizationError } = useOrganization();
    const { data: groups, isPending: isLoadingGroups, error: groupError } = useListGroups();
    const { data: policies, error: projectPolicyError } = useListProjectPolicies(projectId);
    const { data: environmentClasses, error: environmentClassesError } = useListProjectEnvironmentClasses(projectId);

    const orgMembersGroup = useMemo(() => {
        if (!organization || !groups) {
            return;
        }

        return groups.groups.find((g) => g.organizationId === organization.id && g.name === "org-members") ?? null;
    }, [organization, groups]);

    const sharingValue = useMemo(() => {
        if (!policies || !orgMembersGroup) {
            return;
        }

        const policy = policies.policies.find((p) => p.groupId === orgMembersGroup.id);
        return !!policy;
    }, [policies, orgMembersGroup]);

    const isLoading = isLoadingOrganization || isLoadingGroups;
    const error = organizationError || groupError || projectPolicyError || environmentClassesError;

    return {
        isLoading,
        error: error || undefined,
        data:
            !!projectId && organization && orgMembersGroup
                ? {
                      orgId: organization.id,
                      projectId: projectId,
                      orgMembersGroupId: orgMembersGroup.id,
                      shared: typeof sharingValue !== "undefined" ? sharingValue : undefined,
                      environmentClasses: environmentClasses?.environmentClasses,
                  }
                : undefined,
    };
};

export const useListProjectStates = (
    projectIds?: string[],
): { isLoading: boolean; data: ProjectState[] | undefined } => {
    const { data: organization, isPending: isLoadingOrganization } = useOrganization();
    const { data: groups, isPending: isLoadingGroups } = useListGroups();
    const allProjectPolicies = useListProjectsPolicies(projectIds);
    const allProjectEnvironmentClasses = useListProjectsEnvironmentClasses(projectIds);

    const orgMembersGroup = useMemo(() => {
        if (!organization || !groups) {
            return;
        }

        return groups.groups.find((g) => g.organizationId === organization.id && g.name === "org-members") ?? null;
    }, [organization, groups]);

    const sharingState: Record<string, boolean> = useMemo(() => {
        if (orgMembersGroup === undefined || allProjectPolicies.every((i) => !i.data)) {
            return {} as Record<string, boolean>;
        }

        const sharingValues = allProjectPolicies
            .map((r) => {
                if (!r.data) {
                    return undefined;
                }
                const policy = orgMembersGroup
                    ? r.data?.policies.find((p) => p.groupId === orgMembersGroup.id)
                    : undefined;
                return { projectId: r.data?.projectId, sharing: !!policy };
            })
            .filter((i) => !!i);

        return sharingValues.reduce(
            (acc, curr) => {
                acc[curr.projectId] = curr.sharing;
                return acc;
            },
            {} as Record<string, boolean>,
        );
    }, [orgMembersGroup, allProjectPolicies]);

    const projectEnvironmentClassesState: Record<string, PlainProjectEnvironmentClass[]> = useMemo(() => {
        return allProjectEnvironmentClasses.reduce(
            (acc, curr) => {
                if (curr.data) {
                    acc[curr.data.projectId] = curr.data.environmentClasses;
                }
                return acc;
            },
            {} as Record<string, PlainProjectEnvironmentClass[]>,
        );
    }, [allProjectEnvironmentClasses]);

    const isLoading = isLoadingOrganization || isLoadingGroups;

    return {
        isLoading,
        data:
            organization && orgMembersGroup && projectIds
                ? projectIds.map((projectId) => {
                      return {
                          orgId: organization.id,
                          projectId: projectId,
                          orgMembersGroupId: orgMembersGroup.id,
                          shared: typeof sharingState[projectId] !== "undefined" ? sharingState[projectId] : undefined,
                          environmentClasses:
                              typeof projectEnvironmentClassesState[projectId] !== "undefined"
                                  ? projectEnvironmentClassesState[projectId]
                                  : undefined,
                      };
                  })
                : undefined,
    };
};

export const useListProjectEnvironmentClasses = (projectId?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: projectsQueryKey.listEnvironmentClasses(projectId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const environmentClasses = await autoPaginate(
                (params: PartialMessage<ListProjectEnvironmentClassesRequest>) =>
                    api.projectService.listProjectEnvironmentClasses(params),
                { projectId, pagination: { pageSize: 30 } }, // For now max total environment classes per project is 30
                (response) => response.projectEnvironmentClasses,
            );

            return {
                projectId,
                environmentClasses: environmentClasses.map(toPlainProjectEnvironmentClass),
            };
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user && !!projectId,
    });
};

/**
 * Like {@link useListProjectEnvironmentClasses}, but returns a list of {@link EnvironmentClassEntry} for a given project
 * @param projectId The ID of the project that this query is querying for
 */
export const useListProjectEnvironmentClassEntries = ({
    projectId,
    enabled = true,
}: {
    projectId?: string;
    enabled?: boolean;
}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { environmentClasses, isLoading: isLoadingEnvironmentClasses } = useEnvironmentClassMap();

    return useQuery({
        queryKey: projectsQueryKey.listEnvironmentClassEntries(projectId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!environmentClasses) {
                throw new Error("failed to query for environment classes");
            }

            const projectEnvClasses = await autoPaginate(
                (params: PartialMessage<ListProjectEnvironmentClassesRequest>) =>
                    api.projectService.listProjectEnvironmentClasses(params),
                { projectId, pagination: { pageSize: 30 } }, // For now max total environment classes per project is 30
                (response) => response.projectEnvironmentClasses,
            );

            const entries: EnvironmentClassEntry[] = [];
            let localRunnerEntriesAdded = false;
            for (const clazz of projectEnvClasses) {
                switch (clazz.environmentClass.case) {
                    case "localRunner":
                        if (!localRunnerEntriesAdded) {
                            for (const envClass of Object.values(environmentClasses)) {
                                if (envClass.localRunner) {
                                    entries.push(envClass);
                                }
                            }
                            localRunnerEntriesAdded = true;
                        }
                        break;
                    case "environmentClassId": {
                        const entry = environmentClasses[clazz.environmentClass.value];
                        if (entry) {
                            entries.push(entry);
                        }
                        break;
                    }
                }
            }

            return {
                projectId,
                environmentClasses: entries,
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user && !!projectId && !isLoadingEnvironmentClasses && enabled,
    });
};

export const useListProjectsEnvironmentClasses = (projectIds?: string[]) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQueries({
        queries: projectIds
            ? projectIds.map((projectId) => {
                  return {
                      queryKey: projectsQueryKey.listEnvironmentClasses(projectId),
                      queryFn: async () => {
                          if (!user) {
                              throw new Error("User not authenticated");
                          }

                          const environmentClasses = await autoPaginate(
                              (params: PartialMessage<ListProjectEnvironmentClassesRequest>) =>
                                  api.projectService.listProjectEnvironmentClasses(params),
                              { projectId, pagination: { pageSize: 30 } }, // For now max total environment classes per project is 30
                              (response) => response.projectEnvironmentClasses,
                          );

                          return {
                              projectId,
                              environmentClasses: environmentClasses.map(toPlainProjectEnvironmentClass),
                          };
                      },
                      staleTime: 60_000,
                      throwOnError: defaultThrowOnError,
                      retry: defaultRetry,
                  };
              })
            : [],
    });
};

export const useUpdateProjectEnvironmentClasses = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            projectId,
            projectEnvironmentClasses,
        }: {
            projectId: string;
            projectEnvironmentClasses: PlainProjectEnvironmentClass[];
        }) => {
            await api.projectService.updateProjectEnvironmentClasses({
                projectId,
                projectEnvironmentClasses,
            });
        },
        onSuccess: async (_, { projectId }) => {
            await queryClient.invalidateQueries({ queryKey: projectsQueryKey.listEnvironmentClasses(projectId) });
        },
    });
};
